import { useState } from 'react';
import { faSliders } from '@fortawesome/free-solid-svg-icons';

import notificationApi from '@/core/notifications/api/notification-api';
import { Contact, ReceivedBroadcast, Subscription } from '@/core/notifications/model';
import { AppModal, CheckboxRow, Toggle, EyebrowLabel } from '@/shared/components/export';

type Mode = 'off' | 'allowlist' | 'denylist';

export default function SubscriptionFilterPanel({
  contact,
  subscriptions,
  broadcasts,
  onSaved,
  onCancel,
}: {
  contact: Contact;
  subscriptions: Subscription[];
  broadcasts: ReceivedBroadcast[];
  onSaved: (updated: Contact) => void;
  onCancel: () => void;
}) {
  // Only subscriptions/broadcasts that actually deliver via push are relevant
  // here — one that doesn't have push in its channels would never reach this
  // device regardless of the filter, so listing it would just be noise.
  const pushSubscriptions = subscriptions.filter(sub => sub.defaultChannels.includes('push'));
  const pushBroadcasts = broadcasts.filter(
    broadcast =>
      broadcast.defaultChannels.length === 0 || broadcast.defaultChannels.includes('push'),
  );

  const [mode, setMode] = useState<Mode>(contact.subscriptionFilter?.mode ?? 'off');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(contact.subscriptionFilter?.sourceIds ?? []),
  );
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const toggleSource = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage(null);
    try {
      const updated = await notificationApi.patchContact(contact.id, {
        subscriptionFilter: mode === 'off' ? null : { mode, sourceIds: [...selected] },
      });
      onSaved(updated);
    } catch {
      setErrorMessage('Could not save this device’s alarm filter.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      show
      onClose={onCancel}
      className="notifications-settings"
      icon={faSliders}
      title="Manage This Device"
      primaryLabel={saving ? 'Saving…' : 'Save'}
      onPrimary={handleSave}
      primaryDisabled={saving}
    >
      <div className="mb-2" style={{ fontSize: 14 }}>
        <div className="mb-2">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span>Receives everything</span>
            <Toggle checked={mode === 'off'} onChange={() => setMode('off')} />
          </div>
          <div className="mutedText" style={{ fontSize: 14, paddingRight: 46 }}>
            All alarms and broadcasts are delivered to this device.
          </div>
        </div>

        <div className="mb-2">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span>Receives everything except what&apos;s checked below</span>
            <Toggle checked={mode === 'denylist'} onChange={() => setMode('denylist')} />
          </div>
          <div className="mutedText" style={{ fontSize: 14, paddingRight: 46 }}>
            Everything is delivered by default — check items below to exclude them from this device.
          </div>
        </div>

        <div>
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span>Receives only what&apos;s checked below</span>
            <Toggle checked={mode === 'allowlist'} onChange={() => setMode('allowlist')} />
          </div>
          <div className="mutedText" style={{ fontSize: 14, paddingRight: 46 }}>
            New alarms won&apos;t be added here automatically — you&apos;ll need to check them
            manually.
          </div>
        </div>
      </div>

      {mode !== 'off' && (
        <div className="mb-2">
          <div className="mutedText mb-2" style={{ fontSize: 14 }}>
            Only alarms and broadcasts with push notifications turned on are listed below.
          </div>
          {pushSubscriptions.length > 0 && (
            <div className="mb-2">
              <EyebrowLabel muted>Alarms</EyebrowLabel>
              {pushSubscriptions.map(sub => (
                <CheckboxRow
                  key={sub.id}
                  className="mb-1"
                  checked={selected.has(sub.id)}
                  onChange={() => toggleSource(sub.id)}
                  label={sub.name}
                />
              ))}
            </div>
          )}

          {pushBroadcasts.length > 0 && (
            <div>
              <EyebrowLabel muted>Broadcasts</EyebrowLabel>
              {pushBroadcasts.map(broadcast => (
                <CheckboxRow
                  key={broadcast.id}
                  className="mb-1"
                  checked={selected.has(broadcast.id)}
                  onChange={() => toggleSource(broadcast.id)}
                  label={broadcast.name}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {errorMessage && (
        <div className="mb-2" style={{ fontSize: 12, color: 'var(--bs-danger)' }}>
          {errorMessage}
        </div>
      )}
    </AppModal>
  );
}
